import { supabase } from '@/core/database/supabase';
import { permissionEngine } from '@/core/permissions/permissionEngine';
import type { Warehouse, WarehouseCreatePayload, WarehouseUpdatePayload } from '../types/warehouses.types';

export const warehousesService = {
  async fetchWarehouses(companyId: string, search: string, isActive: string | boolean) {
    if (!companyId) return [];

    let query = supabase
      .from('warehouses')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });

    if (search) {
      query = query.or(`name.ilike.%${search}%,code.ilike.%${search}%`);
    }

    if (isActive !== 'all' && isActive !== '') {
      const boolVal = isActive === 'true' || isActive === true;
      query = query.eq('is_active', boolVal);
    }

    const { data, error } = await query;
    if (error) throw error;
    
    // Type casting because supabase foreign key relationships might not perfectly map
    return (data || []) as unknown as Warehouse[];
  },

  async createWarehouse(userId: string, payload: WarehouseCreatePayload) {
    await permissionEngine.assert(userId, 'inventory.warehouses.create', { companyId: payload.company_id });

    const { data, error } = await supabase
      .from('warehouses')
      .insert({
        ...payload,
        is_active: payload.is_active ?? true,
      })
      .select('id')
      .single();

    if (error) throw error;
    return data.id;
  },

  async updateWarehouse(userId: string, companyId: string, id: string, payload: WarehouseUpdatePayload) {
    await permissionEngine.assert(userId, 'inventory.warehouses.update', { companyId });

    const { error } = await supabase
      .from('warehouses')
      .update(payload)
      .eq('id', id)
      .eq('company_id', companyId);

    if (error) throw error;
    return true;
  },

  async deleteWarehouse(userId: string, companyId: string, id: string) {
    await permissionEngine.assert(userId, 'inventory.warehouses.delete', { companyId });

    const { error } = await supabase
      .from('warehouses')
      .delete()
      .eq('id', id)
      .eq('company_id', companyId);

    if (error) throw error;
    return true;
  },
};
